import React, { useEffect, useRef, useState } from "react";
import { Button, Menu, MenuItem, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import styles from "../../../styles/misreports/misreport.module.css";
import BarChart from "../../../Components/GraphNCharts/BarChart";
import CustomTable from "../../../Components/MisReport/Table";
import companyLogo from "../../../assets/images/nirantara_logo_new.svg";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { useUser } from "../../../context/AuthContext";
import { get } from "../../../utils/api";
import { truncateNumber as truncateNumberRaw } from "../../../utils/common";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useParams, useLocation } from "react-router-dom";
import rightIndication from "../../../assets/images/rightIndication.svg";
import MISCoverpage from "../../../assets/images/MISCoverpage.svg";
import vajraLogo from "../../../assets/images/vajra_logo.svg";
import reportHeaderImage from "../../../assets/images/report-header.svg";
import {
  getGhgReportPdfStyles,
  getGhgReportFirstPagePdfCss,
  GHG_PDF_BACKEND_MARGINS,
} from "./GHGReportStyle";

/**
 * Word ignores most CSS on data-uri images but honors width/height.
 * Chart.js canvases are often 2–3× layout size — downscale so the .doc fits the page.
 */
const MIS_TOC_PAGES = [
  {
    pageNumber: "02",
    sections: [
      { title: "1. EXECUTIVE SUMMARY", pages: "04", tone: "neutral" },
      {
        title: "2. ENVIRONMENTAL METRICS",
        pages: "04 - 07",
        tone: "green",
        items: [
          {
            label: "2.1 GHG Emissions",
            page: "05",
            children: [
              ["(a) Direct Emissions", "04"],
              ["(b) Indirect Emissions from Purchased Energy", "04"],
              ["(c) Emission from Value Chain", "04"],
            ],
          },
          { label: "2.2 Energy Consumption", page: "05" },
          { label: "2.3 Water Consumption", page: "05" },
          { label: "2.4 Waste Management", page: "05" },
          {
            label: "2.5 Upstream Transportation",
            page: "06 - 07",
            children: [
              ["(a) Logistics Summary", "06"],
              ["(b) Emissions Intensity", "07"],
            ],
          },
          {
            label: "2.6 Downstream Transportation",
            page: "07",
            children: [
              ["(a) Logistics Summary", "07"],
              ["(b) Emissions Intensity", "07"],
            ],
          },
        ],
      },
      {
        title: "3. SOCIAL METRICS",
        pages: "08",
        tone: "orange",
        items: [
          { label: "3.1 Workforce Diversity", page: "08" },
          { label: "3.2 Employee Engagement", page: "08" },
          { label: "3.3 Health and Safety", page: "08" },
        ],
      },
    ],
  },
  {
    pageNumber: "03",
    decorativeImage: true,
    sections: [
      {
        title: "4. GOVERNANCE METRICS",
        pages: "09",
        tone: "blue",
        items: [
          { label: "4.1 Board Structure & Composition", page: "09" },
          { label: "4.2 Compliance & Ethics", page: "09" },
          { label: "4.3 Shareholder Rights", page: "09" },
          { label: "4.4 Transparency & Disclosure", page: "09" },
        ],
      },
      {
        title: "5. SITE-WISE ANALYSIS",
        pages: "10 - 12",
        tone: "neutral",
        items: [
          {
            label: "5.1 GHG Accounting",
            page: "10",
            children: [
              ["(a) Scope 1", "10"],
              ["(b) Scope 2", "10"],
              ["(c) Scope 3", "11"],
            ],
          },
          {
            label: "5.2 Water",
            page: "12",
            children: [
              ["(a) Water Withdrawal", "12"],
              ["(b) Waste water discharge", "12"],
              ["(c) Waste water Treated", "12"],
            ],
          },
        ],
      },
      { title: "6. CONCLUSION", pages: "13", tone: "neutral" },
    ],
  },
];
function chartCanvasForWordExport(sourceCanvas, maxWidthPx = 640) {
  if (!(sourceCanvas instanceof HTMLCanvasElement)) return null;
  const sw = sourceCanvas.width;
  const sh = sourceCanvas.height;
  if (!sw || !sh) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: Math.max(sw, 1),
        height: Math.max(sh, 1),
      };
    } catch {
      return null;
    }
  }
  const scale = Math.min(1, maxWidthPx / sw);
  const dw = Math.max(1, Math.round(sw * scale));
  const dh = Math.max(1, Math.round(sh * scale));
  if (scale >= 1) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: sw,
        height: sh,
      };
    } catch {
      return null;
    }
  }
  const out = document.createElement("canvas");
  out.width = dw;
  out.height = dh;
  const ctx = out.getContext("2d");
  if (!ctx) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: sw,
        height: sh,
      };
    } catch {
      return null;
    }
  }
  ctx.drawImage(sourceCanvas, 0, 0, sw, sh, 0, 0, dw, dh);
  try {
    return { dataUrl: out.toDataURL("image/png"), width: dw, height: dh };
  } catch {
    return null;
  }
}

const MisReportCard = () => {
  const truncateNumber = (value) => truncateNumberRaw(value, 4);
  const reportRef = useRef();
  const { user } = useUser();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const location = useLocation(); // State to track loading
  const searchParams = new URLSearchParams(location.search);
  const reportYear = searchParams.get("year");

  const [data, setData] = useState({});
  const [scope1BarChartData, setScope1BarChartData] = useState([]);
  const [scope2BarChartData, setScope2BarChartData] = useState([]);
  const [scope3BarChartData, setScope3BarChartData] = useState([]);
  const [scope4BarChartData, setScope4BarChartData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [summary, setSummary] = useState({
    scope1: "",
    scope2: "",
    scope3: "",
    scope4: "",
  });

  const [base64Logo, setBase64Logo] = useState("");

  const convertToBase64 = async (url) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      const blob = await res.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Image conversion failed:", err);
      return "";
    }
  };

  useEffect(() => {
    // Convert image once when component mounts
    convertToBase64(user?.company?.companyImage)
      .then((result) => {
        setBase64Logo(result);
      })
      .catch(console.error);
  }, []);

  const getData = async () => {
    if (id) {
      try {
        setLoading(true);

        const response = await get(`/mis-report/data?id=${id}`);
        setData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false); // Stop loading after response (success or error)
      }
    }
  };

  const getGHGFacilityWiseData = async () => {
    try {
      const response = await get(`/mis-report/ghg?id=${id}`);
      setScope1BarChartData(response?.data?.scope1);
      setScope2BarChartData(response?.data?.scope2);
      setScope3BarChartData(response?.data?.scope3);
      setScope4BarChartData(response?.data?.scope4);
      setSummary({
        scope1: response?.data?.scope1Summary,
        scope2: response?.data?.scope2Summary,
        scope3: response?.data?.scope3Summary,
        scope4: response?.data?.scope4Summary,
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    getData();
    getGHGFacilityWiseData();
    // waterFacilityWiseData();
  }, []);

  const scope1Headers = [
    "Emission Source",
    "Current Month (tCO2e)",
    "Previous Month (tCO2e)",
    "% Change",
    // "Peer Average (tCO2e)",
  ];

  const scope4AvoidanceHeaders = [
    "Avoidance Source",
    "Current Month (tCO2e)",
    "Previous Month (tCO2e)",
    "% Change",
  ];

  const scope4OffsetHeaders = [
    "Offset Source",
    "Current Month (tCO2e)",
    "Previous Month (tCO2e)",
    "% Change",
  ];

  const energyConsumptionHeaders = [
    "Energy Source",
    "Current Month (MJ)",
    "Previous Month (MJ)",
    "% Change",
    // "Peer Average (MWh)",
  ];

  const waterConsumptionHeaders = [
    "Metric",
    "Current Month (m3)",
    "Previous Month (m3)",
    "% Change",
    // "Peer Average (m3)",
  ];

  const wasteManagementHeaders = [
    "Metric",
    "Current Month (tonnes)",
    "Previous Month (tonnes)",
    "% Change",
    // "Peer Average (tonnes)",
  ];

  const logisticSummaryHeaders = [
    "Metric",
    "Current Month",
    "Previous Month",
    "% Change",
  ];

  const emissionIntensityHeaders = [
    "Metric",
    "Current Month",
    "Previous Month",
    "% Change",
    // "Peer Average",
  ];

  const socialMetricsDiversityHeaders = [
    "Metric",
    "Current Month (%)",
    "Previous Month (%)",
    "% Change",
    // "Peer Average (%)",
  ];

  const socialMetricsHeaders = [
    "Metric",
    "Current Month",
    "Previous Month",
    "% Change",
    // "Peer Average",
  ];

  const govtMetricsHeaders = [
    "Metric",
    "Current Quarter",
    "Previous Quarter",
    "% Change",
    // "Peer Average",
  ];

  const formatReportNumber = (value) => {
    const normalizedValue =
      typeof value === "string" ? value.replace(/,/g, "") : value;
    const numericValue = Number(normalizedValue ?? 0);

    if (!Number.isFinite(numericValue)) return "0";

    const roundedValue = Number(numericValue.toFixed(3));
    if (roundedValue === 0) return "0";

    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 3,
      minimumFractionDigits: 0,
    }).format(roundedValue);
  };

  const format2 = formatReportNumber;

  const socialMetricRow = (metric, metricData = {}) => ({
    Metric: metric,

    "Current Month (%)": formatReportNumber(
      metricData.currentMonthTotal ?? metricData.thisMonthTotal,
    ),

    "Previous Month (%)": formatReportNumber(metricData.previousMonthTotal),

    "% Change": formatReportNumber(metricData.percentageChange),
  });
  const hasMetricData = (metricData) => {
    const currentValue = Number(
      metricData?.currentMonthTotal ?? metricData?.thisMonthTotal ?? 0,
    );
    const previousValue = Number(metricData?.previousMonthTotal ?? 0);

    return currentValue !== 0 || previousValue !== 0;
  };

  const trainingRowsFromMetrics = (metrics = {}) =>
    Object.entries(metrics)
      .filter(([, metricData]) => hasMetricData(metricData))
      .map(([metric, metricData]) => socialMetricRow(metric, metricData));

  const numberOfEthnicGroupsMetric = (() => {
    const ethnicGroups = data?.diversityAndInclusion?.numberOfEthnicGroups;
    if (!Array.isArray(ethnicGroups) || ethnicGroups.length === 0) {
      return {
        currentMonthTotal: 0,
        previousMonthTotal: 0,
        percentageChange: 0,
      };
    }

    const currentMonthTotal = ethnicGroups.filter(
      (group) => Number(group?.currentPercentage ?? 0) > 0,
    ).length;
    const previousMonthTotal = ethnicGroups.filter(
      (group) => Number(group?.previousPercentage ?? 0) > 0,
    ).length;

    return {
      currentMonthTotal,
      previousMonthTotal,
      percentageChange:
        previousMonthTotal === 0
          ? currentMonthTotal > 0
            ? 100
            : 0
          : ((currentMonthTotal - previousMonthTotal) / previousMonthTotal) *
            100,
    };
  })();

  const scope1Rows = [
    {
      "Emission Source": "Stationary Combustion",
      "Current Month (tCO2e)": format2(
        data?.scope1Emissions?.stationaryCombustion?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.scope1Emissions?.stationaryCombustion?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.scope1Emissions?.stationaryCombustion?.percentageChange,
      ),
    },
    {
      "Emission Source": "Mobile Combustion",
      "Current Month (tCO2e)": format2(
        data?.scope1Emissions?.mobileCombustion?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.scope1Emissions?.mobileCombustion?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.scope1Emissions?.mobileCombustion?.percentageChange,
      ),
    },
    {
      "Emission Source": "Fugitive Emission",
      "Current Month (tCO2e)": format2(
        data?.scope1Emissions?.fugitiveCombustion?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.scope1Emissions?.fugitiveCombustion?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.scope1Emissions?.fugitiveCombustion?.percentageChange,
      ),
    },
    {
      "Emission Source": "Total Scope 1 Emission",
      "Current Month (tCO2e)": format2(
        data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.scope1Emissions?.totalScope1Combustion?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.scope1Emissions?.totalScope1Combustion?.percentageChange,
      ),
    },
  ];

  const metricRow = (label, metricData = {}) => ({
    "Emission Source": label,
    "Current Month (tCO2e)": format2(metricData.thisMonthTotal),
    "Previous Month (tCO2e)": format2(metricData.previousMonthTotal),
    "% Change": format2(metricData.percentageChange),
  });

  const scope4MetricRow = (sourceKey, label, metricData = {}) => ({
    [sourceKey]: label,
    "Current Month (tCO2e)": format2(metricData.thisMonthTotal),
    "Previous Month (tCO2e)": format2(metricData.previousMonthTotal),
    "% Change": format2(metricData.percentageChange),
  });

  const scope2Rows = [
    metricRow("Electricity", data?.scope2Emissions?.electricity),
    metricRow("Heating and Cooling", data?.scope2Emissions?.heat),
    metricRow("Steam", data?.scope2Emissions?.steam),
    metricRow("Total Scope 2 Emission", data?.scope2Emissions?.totalEmission),
  ];

  const scope3Rows = [
    metricRow(
      "Purchased Goods & Services",
      data?.scope3Emissions?.purchasedGoods,
    ),
    metricRow(
      "Upstream/Downstream Transportation & Distribution",
      data?.scope3Emissions?.upstreamAndDownStream,
    ),
    metricRow(
      "Waste Generated in Operations",
      data?.scope3Emissions?.wasteGenerated,
    ),
    metricRow("Employee Commute", data?.scope3Emissions?.employeeCommute),
    metricRow("Business Travel", data?.scope3Emissions?.businessTravel),
    metricRow("Total Scope 3 Emission", data?.scope3Emissions?.totalEmission),
  ];

  const scope4Rows = [
    scope4MetricRow(
      "Avoidance Source",
      "Avoidance from renewable energy Export",
      data?.scope4Emission?.renewable,
    ),
    scope4MetricRow(
      "Avoidance Source",
      "Low Carbon Product Use/ Cleaner Alternatives",
      data?.scope4Emission?.lowCarbon,
    ),
    scope4MetricRow(
      "Avoidance Source",
      "Total Scope 4 Carbon Avoidance",
      data?.scope4Emission?.avoidanceSourceSum,
    ),
  ];

  const scope4RowsForOffset = [
    scope4MetricRow(
      "Offset Source",
      "In-house Offset Projects-Carbon Sequestered",
      data?.scope4Emission?.inhouse,
    ),
    scope4MetricRow(
      "Offset Source",
      "Offset Credit Purchase Tracking Credit Purchased",
      data?.scope4Emission?.offsetting,
    ),
    scope4MetricRow(
      "Offset Source",
      "Total Scope 4 Carbon Offset",
      data?.scope4Emission?.offsetSourceSum,
    ),
  ];
  const energyConsumptionRows = [
    {
      "Emission Source": "Electricity",
      "Current Month (tCO2e)": format2(
        data?.energyConsumption?.electricity?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.energyConsumption?.electricity?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.energyConsumption?.electricity?.percentageChange,
      ),
      // "Peer Average (tCO2e)": 0,
    },
    {
      "Emission Source": "Heating and Cooling",
      "Current Month (tCO2e)": format2(
        data?.energyConsumption?.heat?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.energyConsumption?.heat?.previousMonthTotal,
      ),
      "% Change": format2(data?.energyConsumption?.heat?.percentageChange),
      // "Peer Average (tCO2e)": 0,
    },
    {
      "Emission Source": "Steam",
      "Current Month (tCO2e)": format2(
        data?.energyConsumption?.steam?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.energyConsumption?.steam?.previousMonthTotal,
      ),
      "% Change": format2(data?.energyConsumption?.steam?.percentageChange),
      // "Peer Average (tCO2e)": 0,
    },
    {
      "Emission Source": "Total Energy",
      "Current Month (tCO2e)": format2(
        data?.energyConsumption?.totalEmission?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.energyConsumption?.totalEmission?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.energyConsumption?.totalEmission?.percentageChange,
      ),
      // "Peer Average (tCO2e)": 0,
    },
  ];

  const waterConsumptionRows = [
    {
      "Emission Source": "Total Water",
      "Current Month (tCO2e)": format2(
        data?.waterConsumption?.totalWater?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.waterConsumption?.totalWater?.previousMonthTotal,
      ),
      "% Change": format2(data?.waterConsumption?.totalWater?.percentageChange),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.waterConsumption?.totalWater?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Water Recycled",
      "Current Month (tCO2e)": format2(
        data?.waterConsumption?.waterRecycled?.thisMonthWaterRecycled,
      ),
      "Previous Month (tCO2e)": format2(
        data?.waterConsumption?.waterRecycled?.previousMonthWaterRecycled,
      ),
      "% Change": format2(
        data?.waterConsumption?.waterRecycled?.percentageChange,
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.waterConsumption?.waterRecycled?.peerAverage ?? 0
      // ),
    },
  ];

  const wasteManagementRows = [
    {
      "Emission Source": "Total Waste",
      "Current Month (tCO2e)": format2(
        data?.wasteManagement?.totalWaste?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.wasteManagement?.totalWaste?.previousMonthTotal,
      ),
      "% Change": format2(data?.wasteManagement?.totalWaste?.percentageChange),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.totalWaste?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Hazardous Waste",
      "Current Month (tCO2e)": format2(
        data?.wasteManagement?.hazardousWaste?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.wasteManagement?.hazardousWaste?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.wasteManagement?.hazardousWaste?.percentageChange,
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.hazardousWaste?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Non-Hazardous Waste",
      "Current Month (tCO2e)": format2(
        data?.wasteManagement?.nonHazardousWaste?.thisMonthTotal,
      ),
      "Previous Month (tCO2e)": format2(
        data?.wasteManagement?.nonHazardousWaste?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.wasteManagement?.nonHazardousWaste?.percentageChange,
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.nonHazardousWaste?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Waste Diverted",
      "Current Month (tCO2e)": format2(
        data?.wasteManagement?.wasteDiverted?.thisMonthTotal ?? 0,
        2,
      ),
      "Previous Month (tCO2e)": format2(
        data?.wasteManagement?.wasteDiverted?.previousMonthTotal ?? 0,
        2,
      ),
      "% Change": format2(
        data?.wasteManagement?.wasteDiverted?.percentageChange ?? 0,
        2,
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.wasteDiverted?.peerAverage ?? 0
      // ),
    },
  ];

  const upstreamLogisticRows = [
    {
      Metric: "Total Number of Trips",
      "Current Month": format2(
        data?.upStreamLogisticSummary?.totalTrips?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.upStreamLogisticSummary?.totalTrips?.previousMonthTotal,
      ),
      Target: format2(
        data?.upStreamLogisticSummary?.totalTrips?.percentageChange,
      ),
    },
    {
      Metric: "Total Mass/volume / SKU (stock keeping units) carried",
      "Current Month": format2(
        data?.upStreamLogisticSummary?.totalMass?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.upStreamLogisticSummary?.totalMass?.previousMonthTotal,
      ),
      Target: format2(
        data?.upStreamLogisticSummary?.totalMass?.percentageChange,
      ),
    },
  ];

  const upstreamEmissionIntensityRows = [
    {
      Metric: "Average Emission Per Trip (tCO2e/Trip)",
      "Current Month": format2(
        data?.upStreamEmissionIntensity?.averageEmissionPerTrip?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.upStreamEmissionIntensity?.averageEmissionPerTrip
          ?.previousMonthTotal,
      ),
      Target: format2(
        data?.upStreamEmissionIntensity?.averageEmissionPerTrip
          ?.percentageChange,
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Mass/Volume (tCO2e/ton or m3)",
      "Current Month": format2(
        data?.upStreamEmissionIntensity?.emissionMass?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.upStreamEmissionIntensity?.emissionMass?.previousMonthTotal,
      ),
      Target: format2(
        data?.upStreamEmissionIntensity?.emissionMass?.percentageChange,
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Distance (tCO2e/km)",
      "Current Month": format2(
        data?.upStreamEmissionIntensity?.emissionDistance?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.upStreamEmissionIntensity?.emissionDistance?.previousMonthTotal,
      ),
      Target: format2(
        data?.upStreamEmissionIntensity?.emissionDistance?.percentageChange,
      ),
      // "Peer Average": 0,
    },
  ];

  const downstreamLogisticRows = [
    {
      Metric: "Total Number of Trips",
      "Current Month": format2(
        data?.downstreamLogisticSummary?.totalTrips?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.downstreamLogisticSummary?.totalTrips?.previousMonthTotal,
      ),
      Target: format2(
        data?.downstreamLogisticSummary?.totalTrips?.percentageChange,
      ),
    },
    {
      Metric: "Total Mass/volume / SKU (stock keeping units) carried",
      "Current Month": format2(
        data?.downstreamLogisticSummary?.totalMass?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.downstreamLogisticSummary?.totalMass?.previousMonthTotal,
      ),
      Target: format2(
        data?.downstreamLogisticSummary?.totalMass?.percentageChange,
      ),
    },
  ];

  const downstreamEmissionIntensityRows = [
    {
      Metric: "Average Emission Per Trip (tCO2e/Trip)",
      "Current Month": format2(
        data?.downStreamEmissionIntensity?.averageEmissionPerTrip
          ?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.downStreamEmissionIntensity?.averageEmissionPerTrip
          ?.previousMonthTotal,
      ),
      Target: format2(
        data?.downStreamEmissionIntensity?.averageEmissionPerTrip
          ?.percentageChange,
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Mass/Volume (tCO2e/ton or m3)",
      "Current Month": format2(
        data?.downStreamEmissionIntensity?.emissionMass?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.downStreamEmissionIntensity?.emissionMass?.previousMonthTotal,
      ),
      Target: format2(
        data?.downStreamEmissionIntensity?.emissionMass?.percentageChange,
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Distance (tCO2e/km)",
      "Current Month": format2(
        data?.downStreamEmissionIntensity?.emissionDistance?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.downStreamEmissionIntensity?.emissionDistance?.previousMonthTotal,
      ),
      Target: format2(
        data?.downStreamEmissionIntensity?.emissionDistance?.percentageChange,
      ),
      // "Peer Average": 0,
    },
  ];

  const socailDiversityRows = [
    {
      Metric: "Gender diversity: Male Employees",
      "Current Month (%)": format2(
        data?.socialWorkforceDiversity?.maleEmployees?.thisMonthTotal,
      ),
      "Previous Month (%)": format2(
        data?.socialWorkforceDiversity?.maleEmployees?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.socialWorkforceDiversity?.maleEmployees?.percentageChange,
      ),
    },
    {
      Metric: "Gender diversity: Female Employees",
      "Current Month (%)": format2(
        data?.socialWorkforceDiversity?.femaleEmployees?.thisMonthTotal ?? 0,
        data?.socialWorkforceDiversity?.femaleEmployees?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.socialWorkforceDiversity?.maleEmployees?.percentageChange,
      ),
      // "Peer Average (%)": Number(
      //   data?.socialWorkforceDiversity?.lessThan30?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Gender diversity: Other Employees",
      "Current Month (%)": format2(
        data?.socialWorkforceDiversity?.otherEmployees?.thisMonthTotal ?? 0,
        data?.socialWorkforceDiversity?.otherEmployees?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.socialWorkforceDiversity?.otherEmployees?.percentageChange,
      ),
      // "Peer Average (%)": Number(
      //   data?.socialWorkforceDiversity?.between30And50?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Ethnicity diversity: Ethnic 1",
      "Current Month (%)": format2(
        data?.socialWorkforceDiversity?.greaterThan50?.thisMonthTotal ?? 0,
      ),
      "Previous Month (%)": format2(
        data?.socialWorkforceDiversity?.greaterThan50?.previousMonthTotal,
      ),
      "% Change": "",
      // "Peer Average (%)": Number(
      //   data?.socialWorkforceDiversity?.greaterThan50?.peerAverage ?? 0
      // ),
    },
  ];
  const employeeWellbeingRows = [
    socialMetricRow("Total Workforce", data?.employeeWellBeing?.totalWorkForce),
    socialMetricRow(
      "Average Training Hours per Employee",
      data?.employeeWellBeing?.avgTrainingHoursPerEmployee,
    ),
    socialMetricRow(
      "Health & Safety Incidents",
      data?.employeeWellBeing?.healthAndSafetyIncidents,
    ),
    socialMetricRow(
      "Lost Time Injury Frequency Rate (LTIFR)",
      data?.employeeWellBeing?.lostTimeInjuryFrequencyRate,
    ),
  ];

  const diversityInclusionRows = [
    socialMetricRow(
      "Gender Diversity Male (%)",
      data?.diversityAndInclusion?.genderDiversity,
    ),
    socialMetricRow(
      "Gender Diversity Female (%)",
      data?.diversityAndInclusion?.femaleDiversityPercetage,
    ),
    socialMetricRow(
      "Number of Ethnic Groups Represented",
      numberOfEthnicGroupsMetric,
    ),
    socialMetricRow(
      "Leadership Diversity (%)",
      data?.diversityAndInclusion?.leadershipDiversity,
    ),
  ];

  const communityEngagementRows = [
    socialMetricRow(
      "Community Projects Sponsored",
      data?.communityEngagement?.communityProjectsSponsored,
    ),
    socialMetricRow(
      "Community Investment (Total Amount Donated to Community Projects)",
      data?.communityEngagement?.totalDonated,
    ),
    socialMetricRow(
      "Employee Volunteer Hours",
      data?.communityEngagement?.empvolunteerHours,
    ),
    socialMetricRow(
      "Local Partnerships",
      data?.communityEngagement?.localOrganizationPartnerships,
    ),
    socialMetricRow(
      "Marginal/Vulnerable Beneficiaries (%)",
      data?.communityEngagement?.marginalBeneficiariesPercentage,
    ),
  ];

  const labourPracticesRows = [
    socialMetricRow(
      "Labour-Related Complaints",
      data?.labourPractices?.labourComplaints,
    ),
    socialMetricRow(
      "Labour Complaint Resolution Rate (%)",
      data?.labourPractices?.labourComplaintsResolved,
    ),
    socialMetricRow(
      "Pending Labour Complaints",
      data?.labourPractices?.labourComplaintsPending,
    ),
    socialMetricRow(
      "POSH Cases Reported",
      data?.labourPractices?.poshCardReported,
    ),
    socialMetricRow(
      "POSH Cases Resolved",
      data?.labourPractices?.poshCardResolved,
    ),
  ];

  const humanRightsRows = [
    socialMetricRow(
      "Human Rights Incidents Reported",
      data?.humanRights?.incidentsReported ??
        data?.humanRights?.incidentsReported ??
        data?.socialHumanRights?.humanRightsIncidentsReported,
    ),
    socialMetricRow(
      "Human Rights Incident Resolution Rate (%)",
      data?.humanRights?.incidentsResolutionRate ??
        data?.humanRights?.incidentResolutionRate ??
        data?.socialHumanRights?.humanRightsIncidentResolutionRate,
    ),
  ];

  const trainingBudgetRow = data?.trainingAndDevelopmentBudget?.budgetAllocated;
  const trainingDevelopmentRows = [
    ...(hasMetricData(trainingBudgetRow)
      ? [socialMetricRow("Training & Development Budget", trainingBudgetRow)]
      : []),
    ...trainingRowsFromMetrics(
      data?.trainingAndDevelopmentBudget?.skillAndAwarenessTrainings
        ?.skillTrainings,
    ),
    ...trainingRowsFromMetrics(
      data?.trainingAndDevelopmentBudget?.skillAndAwarenessTrainings
        ?.awarenessTrainings,
    ),
  ];

  const customerSatisfactionRows = [
    socialMetricRow(
      "Customer Complaints",
      data?.customerSatisfaction?.customerComplaints,
    ),
    socialMetricRow(
      "Complaints Resolved",
      data?.customerSatisfaction?.complaintsResolved,
    ),
    socialMetricRow(
      "Product Recall Rate",
      data?.customerSatisfaction?.productRecallRate,
    ),
    socialMetricRow(
      "Product Safety Incidents",
      data?.customerSatisfaction?.productSafetyIncidents,
    ),
    socialMetricRow("Data Breaches", data?.customerSatisfaction?.dataBreaches),
    socialMetricRow(
      "Data Breach Incidence Rate",
      data?.customerSatisfaction?.dataBreachIncidenceRate,
    ),
    socialMetricRow(
      "Marketing Complaints",
      data?.customerSatisfaction?.marketingComplaints,
    ),
  ];

  const employeeEngagementRows = [
    {
      Metric: "Turnover Rate (%)",
      "Current Month": format2(
        data?.socialEmployeeEngagement?.turnoverRate?.thisMonthTotal ?? 0,
        data?.socialEmployeeEngagement?.turnoverRate?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.socialEmployeeEngagement?.turnoverRate?.percentageChange,
      ),
      // "Peer Average": truncateNumber(
      //   data?.socialEmployeeEngagement?.turnoverRate?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Total Training Hours",
      "Current Month": format2(
        data?.socialEmployeeEngagement?.trainingHours?.thisMonthTotal ?? 0,
        2,
      ),
      "Previous Month": format2(
        data?.socialEmployeeEngagement?.trainingHours?.previousMonthTotal ?? 0,
        2,
      ),
      "% Change": format2(
        data?.socialEmployeeEngagement?.trainingHours?.percentageChange ?? 0,
        2,
      ),
      // "Peer Average": truncateNumber(
      //   data?.socialEmployeeEngagement?.trainingHours?.peerAverage ?? 0
      // ),
    },
  ];

  const healthSafetyRows = [
    {
      Metric:
        "Total Number of employee participated in health and safety training programs",
      "Current Month": format2(
        data?.socialhealthAndSafety?.employeeParticipationPercentage
          ?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.socialhealthAndSafety?.employeeParticipationPercentage
          ?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.socialhealthAndSafety?.employeeParticipationPercentage
          ?.percentageChange,
      ),
    },
    {
      Metric: "Total number of Safety Training Sessions",
      "Current Month": format2(
        data?.socialhealthAndSafety?.trainingSessionsConducted?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.socialhealthAndSafety?.trainingSessionsConducted
          ?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.socialhealthAndSafety?.trainingSessionsConducted
          ?.percentageChange,
      ),
    },
    {
      Metric: "Lost Time Injury Rate",
      "Current Month": format2(
        data?.socialhealthAndSafety?.lostTimeInjuryRate?.thisMonthTotal,
      ),
      "Previous Month": format2(
        data?.socialhealthAndSafety?.lostTimeInjuryRate?.previousMonthTotal,
      ),
      "% Change": format2(
        data?.socialhealthAndSafety?.lostTimeInjuryRate?.percentageChange,
      ),
    },
    {
      Metric: "POSH Cases Reported",
      "Current Month": format2(data?.socialPoshCases?.reported?.thisMonthTotal),
      "Previous Month": format2(
        data?.socialPoshCases?.reported?.previousMonthTotal,
      ),
      "% Change": "",
    },
    {
      Metric: "POSH Cases Resolved",
      "Current Month": format2(data?.socialPoshCases?.resolved?.thisMonthTotal),
      "Previous Month": format2(
        data?.socialPoshCases?.resolved?.previousMonthTotal,
      ),
      "% Change": "",
    },
  ];

  const governanceMetricRow = (metric, metricData = {}) => ({
    Metric: metric,

    "Current Quarter": formatReportNumber(
      metricData.thisMonthTotal ?? metricData.currentMonthTotal,
    ),

    "Previous Quarter": formatReportNumber(metricData.previousMonthTotal),

    "% Change": formatReportNumber(metricData.percentageChange),
  });

  const boardCompositionRows = [
    governanceMetricRow(
      "Total Board Members",
      data?.governmentBoardComposition?.totalBoardMembers,
    ),
    governanceMetricRow(
      "Independent Board Members",
      data?.governmentBoardComposition?.independentDirectors,
    ),
    governanceMetricRow(
      "Female Board Representation",
      data?.governmentBoardComposition?.femaleBoardMembers,
    ),
    governanceMetricRow(
      "Minority Board Representation",
      data?.governmentBoardComposition?.minorityGroupBoardMembers,
    ),
    governanceMetricRow(
      "Average Tenure of Exiting Board Members (Years)",
      data?.governmentBoardComposition?.averageTenureOfExitingBoardMembers,
    ),
  ];

  const complianceEthicsRows = [
    governanceMetricRow(
      "Significant Instances of Non-compliance",
      data?.complianceAndEthics?.complianceViolations,
    ),
    governanceMetricRow(
      "Instances with Monetary Fines",
      data?.complianceAndEthics?.instancesWithMonetaryFines,
    ),
    governanceMetricRow(
      "Instances with Non-monetary Sanctions",
      data?.complianceAndEthics?.instancesWithNonMonetarySanctions,
    ),
    governanceMetricRow(
      "Employees Trained in Ethics (%)",
      data?.complianceAndEthics?.employeesTrainedPercentage,
    ),
  ];

  const shareholderRightsRows = [
    governanceMetricRow(
      "Shareholder Meetings",
      data?.shareholderRights?.shareholderMeetings,
    ),
    governanceMetricRow(
      "Shareholder Voting Participation Rate (%)",
      data?.shareholderRights?.shareholderVotingParticipationRate,
    ),
  ];

  const transparencyDisclosureRows = [
    governanceMetricRow(
      "External Audits Conducted",
      data?.transparencyAndDisclosure?.externalAuditsConducted,
    ),
  ];

  let barCharDataSet = [
    {
      label: "Independent Directors",
      data: [65, 50],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Meeting Attendance Rate",
      data: [85, 70],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  const percentageChangeDataset = (chartData = []) =>
    chartData?.some((item) => item?.percentageChange !== undefined)
      ? [
          {
            label: "% Change",
            data:
              chartData?.map((item) => Number(item?.percentageChange ?? 0)) ??
              [],
            backgroundColor: "rgba(249, 115, 22, 1)",
            borderColor: "rgba(249, 115, 22, 1)",
            borderWidth: 1,
            barThickness: 45,
            maxBarThickness: 45,
          },
        ]
      : [];

  const facilityEmissionDatasets = (chartData = []) => [
    {
      label: "Current Month",
      data: chartData?.map((item) => item.thisMonthEmission) ?? [],
      backgroundColor: "#1E40AF",
      borderColor: "#1E40AF",
      borderWidth: 1,
      barThickness: 45,
      maxBarThickness: 45,
    },
    {
      label: "Previous Month",
      data: chartData?.map((item) => item.previousMonthEmission) ?? [],
      backgroundColor: "#93C5FD",
      borderColor: "#93C5FD",
      borderWidth: 1,
      barThickness: 45,
      maxBarThickness: 45,
    },
    ...percentageChangeDataset(chartData),
  ];
  const calculatePercentageChange = (currentValue, previousValue) => {
    const current = Number(currentValue ?? 0);
    const previous = Number(previousValue ?? 0);

    if (!Number.isFinite(current) || !Number.isFinite(previous)) return 0;
    if (previous === 0) return current > 0 ? 100 : 0;

    return ((current - previous) / previous) * 100;
  };

  const facilityWaterDatasets = (metricKey) => {
    const chartData =
      data?.misReportFacilityWise?.waterFacilitySummary?.map((item) => {
        const thisMonthValue = item?.thisMonth?.[metricKey] ?? 0;
        const previousMonthValue = item?.previousMonth?.[metricKey] ?? 0;

        return {
          ...item,
          thisMonthEmission: Number(thisMonthValue ?? 0),
          previousMonthEmission: Number(previousMonthValue ?? 0),
          percentageChange: calculatePercentageChange(
            thisMonthValue,
            previousMonthValue,
          ),
        };
      }) ?? [];

    return facilityEmissionDatasets(chartData);
  };

  let scope1Data = facilityEmissionDatasets(scope1BarChartData);
  let scope2Data = facilityEmissionDatasets(scope2BarChartData);
  let scope3Data = facilityEmissionDatasets(scope3BarChartData);
  let scope4Data = facilityEmissionDatasets(scope4BarChartData);
  let waterDischargeData = facilityWaterDatasets("totalWaterDischarged");
  let waterWithdrawalData = facilityWaterDatasets("totalWaterWithdrawal");
  let waterTreatedData = facilityWaterDatasets("totalWastewaterTreated");
  let waterCircularityData = facilityWaterDatasets("totalWaterRecycled");

  const open = Boolean(anchorEl);
  const downloadReport = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const waitForImagesToLoad = (element) => {
    const images = element.querySelectorAll("img");
    const promises = Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = img.onerror = resolve;
      });
    });
    return Promise.all(promises);
  };

  // const downloadReportPdf = async () => {
  //   handleClose();

  //   const input = reportRef.current;
  //   if (!input) {
  //     console.error("❌ reportRef is null");
  //     return;
  //   }

  //   await waitForImagesToLoad(input);

  //   const canvas = await html2canvas(input, { scale: 2, useCORS: true });
  //   const fullImageHeight = canvas.height;
  //   const fullImageWidth = canvas.width;

  //   const pageBreakEls = input.querySelectorAll(`.${styles.pageBreak}`);
  //   const pageBreakOffsets = Array.from(pageBreakEls).map((el) => {
  //     const rect = el.getBoundingClientRect();
  //     const parentRect = input.getBoundingClientRect();
  //     return Math.round(
  //       (rect.top - parentRect.top) * (canvas.height / input.clientHeight)
  //     );
  //   });

  //   const pdf = new jsPDF("p", "mm", "a4");
  //   const margin = 10;
  //   const pageWidth = 210;
  //   const pageHeight = 297;

  //   const pdfContentWidth = pageWidth - 2 * margin;
  //   const pdfContentHeight = pageHeight - 2 * margin;

  //   const slices = [];
  //   let startY = 0;

  //   // Add last canvas height if needed
  //   if (
  //     pageBreakOffsets.length === 0 ||
  //     pageBreakOffsets[pageBreakOffsets.length - 1] < fullImageHeight
  //   ) {
  //     pageBreakOffsets.push(fullImageHeight);
  //   }

  //   pageBreakOffsets.forEach((offsetY) => {
  //     const sliceHeight = offsetY - startY;

  //     const sliceCanvas = document.createElement("canvas");
  //     sliceCanvas.width = fullImageWidth;
  //     sliceCanvas.height = sliceHeight;

  //     const ctx = sliceCanvas.getContext("2d");
  //     ctx.drawImage(
  //       canvas,
  //       0,
  //       startY,
  //       fullImageWidth,
  //       sliceHeight,
  //       0,
  //       0,
  //       fullImageWidth,
  //       sliceHeight
  //     );

  //     const imgData = sliceCanvas.toDataURL("image/png");

  //     const scaledHeight = (sliceHeight * pdfContentWidth) / fullImageWidth;

  //     if (slices.length > 0) pdf.addPage();
  //     pdf.addImage(
  //       imgData,
  //       "PNG",
  //       margin,
  //       margin,
  //       pdfContentWidth,
  //       scaledHeight
  //     );

  //     slices.push(imgData);
  //     startY = offsetY;
  //   });

  //   pdf.save("MIS_Report_With_PageBreaks.pdf");
  // };

  // const downloadReportPdf = () => {
  //   handleClose();

  //   const element = reportRef.current;
  //   if (!element) {
  //     console.error("reportRef is null");
  //     return;
  //   }

  //   element.classList.add("pdf-mode");

  //   const opt = {
  //     margin: [10, 7.5, 10, 7.5],
  //     filename: "MIS_Report.pdf",
  //     image: { type: "jpeg", quality: 0.98 },
  //     html2canvas: { scale: 3, useCORS: true },
  //     jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  //     pagebreak: { mode: ["css", "legacy"] },
  //   };

  //   html2pdf()
  //     .set(opt)
  //     .from(element)
  //     .toPdf()
  //     .get("pdf")
  //     .then((pdf) => {
  //       const totalPages = pdf.internal.getNumberOfPages();
  //       const pageWidth = pdf.internal.pageSize.getWidth();
  //       const pageHeight = pdf.internal.pageSize.getHeight();

  //       for (let i = 1; i <= totalPages; i++) {
  //         pdf.setPage(i);
  //         pdf.setFontSize(10);
  //         pdf.text(
  //           `Page ${i} of ${totalPages}`,
  //           pageWidth - 40, // Adjust for right alignment
  //           pageHeight - 10, // Bottom position
  //         );
  //       }

  //       pdf.save("MIS_Report.pdf");
  //     })
  //     .finally(() => {
  //       element.classList.remove("pdf-mode");
  //     });
  // };
  // Helper function to convert image to base64 data URL
  const loadImageAsDataUrl = (src) => {
    if (!src) return null;

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };
      img.onerror = reject;
      img.src = src;
    });
  };
  const convertSvgToPng = (svgSrc, width = 200, height = 60) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          // Draw white background for transparency
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, width, height);
          // Draw the SVG image scaled to fit
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/png"));
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };
      img.onerror = (e) => {
        console.error("Error loading SVG:", e);
        reject(new Error("Failed to load SVG image"));
      };
      img.src = svgSrc;
    });
  };

  // Helper function to convert all images in HTML to base64
  const convertImagesToBase64 = async (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    for (const img of Array.from(images)) {
      const src = img.getAttribute("src");
      if (src && !src.startsWith("data:")) {
        try {
          const dataUrl = await loadImageAsDataUrl(src);
          img.setAttribute("src", dataUrl);
        } catch (error) {
          console.warn(`Could not convert image ${src} to base64:`, error);
        }
      }
    }

    return doc.documentElement.outerHTML;
  };

  const applyGhgCoverFullPageForPdf = (htmlContent) => {
    const { top, right, bottom, left } = GHG_PDF_BACKEND_MARGINS;
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div id="ghg-pdf-root">${htmlContent}</div>`,
      "text/html",
    );
    const root = doc.getElementById("ghg-pdf-root");
    if (!root) return htmlContent;

    const cover = root.querySelector('[data-pdf-cover="true"]');
    if (!cover) return htmlContent;

    const wrapper = doc.createElement("div");
    wrapper.setAttribute("data-ghg-pdf-cover-wrapper", "true");
    cover.parentNode.insertBefore(wrapper, cover);
    wrapper.appendChild(cover);

    wrapper.style.boxSizing = "border-box";
    wrapper.style.position = "relative";
    wrapper.style.width = "210mm";
    wrapper.style.height = "297mm";
    wrapper.style.margin = `-${top} -${right} -${bottom} -${left}`;
    wrapper.style.padding = "0";
    wrapper.style.overflow = "hidden";
    wrapper.style.borderRadius = "0";

    const bgImg = cover.querySelector('[data-pdf-cover-image="true"]');
    const bgSrc = bgImg?.getAttribute("src")?.trim();

    cover.style.width = "100%";
    cover.style.height = "100%";
    cover.style.margin = "0";
    cover.style.padding = "0";
    cover.style.borderRadius = "0";
    cover.style.overflow = "hidden";
    cover.style.position = "relative";
    cover.style.boxSizing = "border-box";

    if (bgSrc) {
      cover.style.backgroundImage = `url("${bgSrc}")`;
      cover.style.backgroundSize = "cover";
      cover.style.backgroundPosition = "center center";
      cover.style.backgroundRepeat = "no-repeat";
    }

    if (bgImg) {
      bgImg.style.position = "absolute";
      bgImg.style.top = "0";
      bgImg.style.left = "0";
      bgImg.style.width = "100%";
      bgImg.style.height = "100%";
      bgImg.style.objectFit = "cover";
      bgImg.style.objectPosition = "center center";
      bgImg.style.maxWidth = "none";
      bgImg.style.display = "block";
      bgImg.style.zIndex = "0";
    }

    const overlay = cover.querySelector('div[class*="cover_overlay"]');
    if (overlay) {
      overlay.style.position = "absolute";
      overlay.style.top = "0";
      overlay.style.left = "0";
      overlay.style.width = "100%";
      overlay.style.height = "100%";
      overlay.style.zIndex = "1";
    }

    const content = cover.querySelector('div[class*="cover_content"]');
    if (content) {
      content.style.position = "absolute";
      content.style.top = "0";
      content.style.left = "0";
      content.style.width = "100%";
      content.style.height = "100%";
      content.style.zIndex = "2";
      content.style.boxSizing = "border-box";
    }

    return root.innerHTML;
  };

  const downloadReportPdf = async () => {
    handleClose();
    try {
      const element = reportRef.current;

      if (!element) {
        console.error("Report container not found");
        return;
      }

      // Load logo image as data URL for header
      // Backend will use fallback if logoDataUrl is not provided
      let logoDataUrl;
      try {
        const logoSrc = user?.groupManager
          ? user?.groupCompanyId?.image
          : user?.company?.companyImage;
        if (logoSrc) {
          logoDataUrl = await loadImageAsDataUrl(logoSrc);
        }
      } catch (error) {
        console.warn("Could not load logo image:", error);
        // Backend will use fallback logo if this fails
      }

      // Load powered by logo as PNG data URL for backend
      let poweredByLogoDataUrl;
      try {
        poweredByLogoDataUrl = await convertSvgToPng(companyLogo, 200, 60);
      } catch (error) {
        console.error("Could not load powered by logo:", error);
      }

      // Make sure the cover and report images are decoded before cloning them.
      // Without this, the backend can receive an empty image during PDF export.
      await waitForImagesToLoad(element);

      let vajraLogoDataUrl;
      try {
        vajraLogoDataUrl = await convertSvgToPng(vajraLogo, 200, 60);
      } catch (error) {
        console.warn("Could not convert Vajra logo for PDF:", error);
      }

      // The PDF service rejects tiny placeholder images and restores its fallback
      // logo. Match its expected 200x60 logo dimensions with a transparent PNG.
      const transparentLogoCanvas = document.createElement("canvas");
      transparentLogoCanvas.width = 200;
      transparentLogoCanvas.height = 60;
      const transparentLogoDataUrl =
        transparentLogoCanvas.toDataURL("image/png");

      // Wait for charts to fully render
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Convert all canvas elements (charts) to images before cloning
      const canvases = Array.from(element.querySelectorAll("canvas"));
      const canvasDataUrls = [];

      for (const canvas of canvases) {
        try {
          // Get canvas data URL with high quality
          const dataUrl = canvas.toDataURL("image/png", 1.0);

          // Get dimensions
          const width = canvas.width || canvas.offsetWidth || 400;
          const height = canvas.height || canvas.offsetHeight || 300;

          canvasDataUrls.push({
            dataUrl,
            width,
            height,
            styleWidth: canvas.style.width || `${width}px`,
            styleHeight: canvas.style.height || `${height}px`,
          });
        } catch (error) {
          console.warn("Could not convert canvas to image:", error);
          canvasDataUrls.push(null);
        }
      }

      // Clone the element after charts are rendered
      const clonedElement = element.cloneNode(true);
      clonedElement
        .querySelectorAll('img[alt*="logo" i]')
        .forEach((logo) => logo.remove());
      clonedElement.style.padding = "0";
      clonedElement.style.margin = "0";

      // Backend PDF rendering cannot resolve bundled SVG URLs reliably.
      // Replace cover branding with already-rasterized PNG data URLs.
      clonedElement.querySelectorAll('img[alt="Vajra logo"]').forEach((img) => {
        if (vajraLogoDataUrl) img.setAttribute("src", vajraLogoDataUrl);
      });
      clonedElement
        .querySelectorAll('img[alt="Nirantara logo"]')
        .forEach((img) => {
          if (poweredByLogoDataUrl)
            img.setAttribute("src", poweredByLogoDataUrl);
        });

      // Cover/TOC are hidden on screen only — ensure they appear in PDF
      const hidePageClass = styles.hidepage;
      clonedElement
        .querySelectorAll(`[class~="${hidePageClass}"]`)
        .forEach((el) => el.classList.remove(hidePageClass));

      clonedElement
        .querySelectorAll("[data-ghg-report-edit-ui]")
        .forEach((el) => el.remove());

      // Replace canvas elements with img elements in cloned element
      const clonedCanvases = Array.from(
        clonedElement.querySelectorAll("canvas"),
      );
      clonedCanvases.forEach((clonedCanvas, index) => {
        const imageData = canvasDataUrls[index];
        if (imageData && imageData.dataUrl) {
          const img = document.createElement("img");
          img.setAttribute("src", imageData.dataUrl); // Use setAttribute to ensure it's in HTML
          img.style.width = imageData.styleWidth;
          img.style.height = imageData.styleHeight;
          img.style.maxWidth = "100%";
          img.style.display = "block";
          img.setAttribute("alt", "Chart");

          // Replace canvas with img
          if (clonedCanvas.parentNode) {
            clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
          }
        }
      });

      // Remove download dropdown from cloned element
      const downloadDropdown =
        clonedElement.querySelector(".download-dropdown");
      if (downloadDropdown) {
        downloadDropdown.remove();
      }

      // Remove "Powered by" footer section from cloned element (for PDF only)
      // Note: This only removes from clonedElement, not the original element
      // The footer remains visible in the regular view, only removed in PDF download
      const poweredBySection =
        clonedElement.querySelector("#company_footer") ||
        clonedElement.querySelector(".company_footer");
      if (poweredBySection) {
        poweredBySection.remove();
      }

      // Convert all images in the HTML to base64 (including the chart images we just created)
      let htmlContent = clonedElement.innerHTML;
      htmlContent = await convertImagesToBase64(`<div>${htmlContent}</div>`);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      htmlContent = doc.body.innerHTML;
      htmlContent = applyGhgCoverFullPageForPdf(htmlContent);

      /* First-page cover CSS must be last so it overrides module/legacy rules */
      const cssStyles = `${getGhgReportPdfStyles()}\n${getGhgReportFirstPagePdfCss()}`;

      // Get FULL HTML of the report component with all CSS styles
      const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
          />
          <style>
            ${cssStyles}
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;

      // Render the first page locally at exact A4 dimensions. The backend PDF
      // clips content to its print margins, so this cover is merged in later.
      const coverSourceDoc = new DOMParser().parseFromString(
        htmlContent,
        "text/html",
      );
      const coverSource =
        coverSourceDoc.querySelector('[data-ghg-pdf-cover-wrapper="true"]') ||
        coverSourceDoc.querySelector('[data-pdf-cover="true"]');
      if (!coverSource) throw new Error("PDF cover source was not found");

      const coverRenderHost = document.createElement("div");
      coverRenderHost.style.position = "fixed";
      coverRenderHost.style.left = "-10000px";
      coverRenderHost.style.top = "0";
      coverRenderHost.style.width = "210mm";
      coverRenderHost.style.height = "297mm";
      coverRenderHost.style.overflow = "hidden";
      coverRenderHost.style.background = "#fff";
      coverRenderHost.innerHTML = `<style>${cssStyles}</style>${coverSource.outerHTML}`;
      document.body.appendChild(coverRenderHost);

      let coverPngDataUrl;
      try {
        await waitForImagesToLoad(coverRenderHost);
        const renderedCover = coverRenderHost.querySelector(
          '[data-ghg-pdf-cover-wrapper="true"], [data-pdf-cover="true"]',
        );
        const coverCanvas = await html2canvas(renderedCover, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          width: renderedCover.scrollWidth,
          height: renderedCover.scrollHeight,
          logging: false,
        });
        coverPngDataUrl = coverCanvas.toDataURL("image/png", 1);
      } finally {
        coverRenderHost.remove();
      }

      // Get base URL and remove /api/v1 suffix for PDF generation endpoint
      const apiBaseUrl =
        sessionStorage.getItem("apiBaseUrl") ||
        // api ||
        "https://uat-api.nirantara.ai";
      const baseUrl = apiBaseUrl.replace(/\/api\/v1$/, "");

      const response = await fetch(`${baseUrl}/mis-generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          html,
          css: cssStyles,
          // Suppress both backend-managed logos; their fixed placement overlaps
          // table rows at page boundaries. A valid PNG prevents fallback artwork.
          logoDataUrl: transparentLogoDataUrl,
          skipFirstPageHeader: true,
          poweredByLogoDataUrl: transparentLogoDataUrl,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(
          `Failed to generate PDF (${response.status}): ${errorBody}`,
        );
      }

      const responseContentType = response.headers.get("content-type") ?? "";
      let backendPdfBytes;

      if (responseContentType.includes("application/json")) {
        const responsePayload = await response.json();
        const generatedPdfUrl =
          responsePayload?.url ??
          responsePayload?.data?.url ??
          responsePayload?.pdfUrl;

        if (!generatedPdfUrl) {
          throw new Error("PDF service response did not include a PDF URL");
        }

        const generatedPdfResponse = await fetch(generatedPdfUrl);
        if (!generatedPdfResponse.ok) {
          throw new Error(
            `Failed to fetch generated PDF (${generatedPdfResponse.status})`,
          );
        }
        backendPdfBytes = await generatedPdfResponse.arrayBuffer();
      } else {
        backendPdfBytes = await response.arrayBuffer();
      }

      // Replace the backend first page with the locally rendered full-bleed cover.
      const backendPdf = await PDFDocument.load(backendPdfBytes);
      const mergedPdf = await PDFDocument.create();
      const a4Width = 595.28;
      const a4Height = 841.89;
      const coverPage = mergedPdf.addPage([a4Width, a4Height]);
      const coverImageBytes = await fetch(coverPngDataUrl).then((res) =>
        res.arrayBuffer(),
      );
      const embeddedCover = await mergedPdf.embedPng(coverImageBytes);
      coverPage.drawImage(embeddedCover, {
        x: 0,
        y: 0,
        width: a4Width,
        height: a4Height,
      });

      if (backendPdf.getPageCount() > 1) {
        const remainingIndices = Array.from(
          { length: backendPdf.getPageCount() - 1 },
          (_, index) => index + 1,
        );
        const remainingPages = await mergedPdf.copyPages(
          backendPdf,
          remainingIndices,
        );
        remainingPages.forEach((page) => mergedPdf.addPage(page));
      }

      // PAGE TOP-GAP RULE: leave pages 01-04 untouched. Scale page 05 onward
      // into the space below a 12mm top gap so no content is cropped.
      const topGapPoints = (12 / 25.4) * 72;
      mergedPdf
        .getPages()
        .slice(4)
        .forEach((page) => {
          const { height } = page.getSize();
          page.scaleContent(1, (height - topGapPoints) / height);
        });

      // Reload after page transforms so footer drawing uses normal coordinates.
      const adjustedPdfBytes = await mergedPdf.save();
      const finalPdf = await PDFDocument.load(adjustedPdfBytes);
      const footerFont = await finalPdf.embedFont(StandardFonts.Helvetica);
      const footerHeight = 38;
      const gradientStops = [
        { offset: 0, color: [248, 173, 88] },
        { offset: 0.25, color: [169, 130, 63] },
        { offset: 0.52, color: [70, 83, 43] },
        { offset: 0.72, color: [18, 63, 59] },
        { offset: 1, color: [31, 79, 114] },
      ];
      const interpolateFooterColor = (position) => {
        const endIndex = gradientStops.findIndex(
          (stop) => stop.offset >= position,
        );
        const end = gradientStops[Math.max(1, endIndex)];
        const start = gradientStops[Math.max(0, endIndex - 1)];
        const amount = (position - start.offset) / (end.offset - start.offset);
        return start.color.map(
          (channel, index) =>
            (channel + (end.color[index] - channel) * amount) / 255,
        );
      };

      finalPdf.getPages().forEach((page, pageIndex) => {
        const { width } = page.getSize();
        const stripeCount = 80;
        const stripeWidth = width / stripeCount;

        for (let stripe = 0; stripe < stripeCount; stripe += 1) {
          const [red, green, blue] = interpolateFooterColor(
            stripe / (stripeCount - 1),
          );
          page.drawRectangle({
            x: stripe * stripeWidth,
            y: 0,
            width: stripeWidth + 0.5,
            height: footerHeight,
            color: rgb(red, green, blue),
          });
        }

        const pageNumber = String(pageIndex + 1).padStart(2, "0");
        page.drawText("Powered by", {
          x: 32,
          y: 14,
          size: 9,
          font: footerFont,
          color: rgb(1, 1, 1),
        });
        page.drawText(pageNumber, {
          x: width - 48,
          y: 12,
          size: 14,
          font: footerFont,
          color: rgb(1, 1, 1),
        });
      });

      const mergedPdfBytes = await finalPdf.save();
      const pdfBlob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "MIS_Report.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF download error:", error);
    }
  };

  const downloadReportWord = () => {
    handleClose();
    const input = reportRef.current;
    if (!input) {
      console.error("❌ reportRef is null");
      return;
    }

    const buildWordHtml = () => {
      const clone = input.cloneNode(true);
      clone
        .querySelectorAll('img[alt*="logo" i]')
        .forEach((logo) => logo.remove());
      const sourceCanvases = input.querySelectorAll("canvas");
      const cloneCanvasList = [...clone.querySelectorAll("canvas")];

      sourceCanvases.forEach((sourceCanvas, index) => {
        const target = cloneCanvasList[index];
        if (!target || !(sourceCanvas instanceof HTMLCanvasElement)) return;
        const exported = chartCanvasForWordExport(sourceCanvas, 640);
        if (!exported) return;
        try {
          const img = document.createElement("img");
          img.src = exported.dataUrl;
          img.alt = "Chart";
          img.setAttribute("width", String(exported.width));
          img.setAttribute("height", String(exported.height));
          target.replaceWith(img);
        } catch (err) {
          console.warn("Could not export chart canvas to Word:", err);
        }
      });

      let htmlContent = clone.innerHTML;

      const pageBreakHTML = "<br style='page-break-before: always;'>";

      // Add page break before large tables (scrollHeight > 800)
      const tables = input.querySelectorAll("table");
      tables.forEach((table) => {
        if (table.scrollHeight > 800) {
          htmlContent = htmlContent.replace(
            table.outerHTML,
            pageBreakHTML + table.outerHTML,
          );
        }
      });

      // Add page break before scope headings
      htmlContent = htmlContent.replace(
        /(<h3>Scope \d+:.*?<\/h3>)/g,
        `${pageBreakHTML}$1`,
      );

      const header = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office'
            xmlns:w='urn:schemas-microsoft-com:office:word'
            xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>Document</title>
          <style>
            table {
              font-size: 12px;
              border-collapse: collapse;
              width: 100%;
              page-break-inside: avoid; /* Avoid splitting table */
            }
            thead {
              display: table-header-group;
            }
            tfoot {
              display: table-footer-group;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            td, th {
              border: 1px solid #000;
              padding: 4px;
              text-align: left;
            }
          </style>
        </head>
        <body>
    `;

      const footer = "</body></html>";

      const sourceHTML = header + htmlContent + footer;

      const blob = new Blob(["\ufeff", sourceHTML], {
        type: "application/msword",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "MIS_Report.doc";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    // Chart.js renders to canvas; innerHTML omits pixels. Wait a frame so charts are painted, then rasterize canvases to PNG for Word.
    requestAnimationFrame(() => {
      requestAnimationFrame(buildWordHtml);
    });
  };
  const TableSectionHeading = ({ children }) => (
    <p className={styles.table_section_heading}>
      <img src={rightIndication} alt="" />
      {children}
    </p>
  );

  return (
    <>
      <div
        ref={reportRef}
        style={{ padding: "1rem" }}
        className={styles.report_content}
      >
        <section
          data-pdf-cover="true"
          className={`${styles.toc_page} ${styles.hidepage}`}
        >
          {/* <img
            src={MISCoverpage}
            alt=""
            className={styles.cover_bg}
            aria-hidden="true"
          /> */}

          <img
            src={MISCoverpage}
            alt="Forest and river landscape"
            className={styles.cover_bg}
            data-pdf-cover-image="true"
          />
          <header data-pdf-cover-header="true">
            <img src={vajraLogo} alt="Vajra logo" />
            {base64Logo ? (
              <img src={base64Logo} alt="Company logo" />
            ) : (
              <strong>{user?.company?.companyName ?? "Company"}</strong>
            )}
          </header>
          <div className={styles.cover_text} data-pdf-cover-text="true">
            <h1>MIS Report</h1>
            <div data-pdf-cover-accent="true">
              <span />
              <span />
              <span />
            </div>
            <div data-pdf-cover-divider="true" />
            <div data-pdf-cover-details="true">
              <div>
                <span>COMPANY NAME</span>
                <strong>{user?.company?.companyName ?? "-"}</strong>
              </div>
              <div>
                <span>REPORTING NAME</span>
                <strong>
                  {[data?.reportingMonth, reportYear]
                    .filter(Boolean)
                    .join(" ") || "-"}
                </strong>
              </div>
              <div>
                <span>DATE</span>
                <strong>
                  {data?.updatedAt
                    ? new Date(data.updatedAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })
                    : "-"}
                </strong>
              </div>
            </div>
          </div>
          <footer data-pdf-cover-footer="true">
            <span>Powered by</span>
            <img src={companyLogo} alt="Nirantara logo" />
            <strong>01</strong>
          </footer>
        </section>

        {MIS_TOC_PAGES.map((tocPage, tocPageIndex) => (
          <section
            key={tocPage.pageNumber}
            data-pdf-toc="true"
            className={`${styles.toc_page} ${styles.hidepage}`}
          >
            <div className={styles.toc_top_rule} aria-hidden="true" />

            <header className={styles.toc_brand_header}>
              <img src={companyLogo} alt="Nirantara logo" />
            </header>

            {tocPageIndex === 0 ? (
              <div className={styles.toc_heading_block}>
                <h2>TABLE OF CONTENTS</h2>
                <div aria-hidden="true">
                  <span />
                  <span />
                </div>
              </div>
            ) : null}

            <div
              className={styles.toc_sections}
              data-pdf-toc-sections="true"
            >
              {tocPage.sections.map((section) => (
                <div
                  className={styles.toc_section}
                  data-pdf-toc-section="true"
                  key={section.title}
                >
                  <div
                    className={`${styles.toc_section_bar} ${styles[`toc_section_${section.tone}`]}`}
                    data-pdf-toc-section-bar="true"
                  >
                    <strong>{section.title}</strong>
                    <strong>{section.pages}</strong>
                  </div>
                  {section.items?.map((item) => (
                    <div
                      className={styles.toc_entry_group}
                      data-pdf-toc-entry-group="true"
                      key={item.label}
                    >
                      <div
                        className={styles.toc_entry}
                        data-pdf-toc-entry="true"
                      >
                        <span>{item.label}</span>
                        <span>{item.page}</span>
                      </div>
                      {item.children?.map(([label, page]) => (
                        <div
                          className={`${styles.toc_entry} ${styles.toc_child_entry}`}
                          data-pdf-toc-entry="true"
                          data-pdf-toc-child-entry="true"
                          key={label}
                        >
                          <span>{label}</span>
                          <span>{page}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>
        ))}
        {base64Logo ? (
          <img src={base64Logo} alt="Company Logo" className={styles.logo} />
        ) : null}
        <div
          // style={{
          //   display: "flex",
          //   justifyContent: "space-between",
          //   alignItems: "center",
          //   marginTop: "1.5rem",
          //   padding: "0 1rem",
          // }}

          className={styles.report_header}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "0 1rem",
          }}
          data-pdf-report-banner="true"
        >
          <img
            src={reportHeaderImage}
            alt="Forest landscape"
            className={styles.report_header_image}
            data-pdf-report-banner-image="true"
          />
          {/* <p
          style={{
            fontFamily: "Inter",
            fontSize: "22px",
            fontWeight: "700",
          }}
        >
          Monthly MIS Report
        </p> */}
          <div
            className={styles.report_title}
            data-pdf-report-banner-title="true"
          >
            <h1>Monthly MIS Report</h1>
            <div className={styles.header_lines}>
              <span className={styles.orange_line} />
              <span className={styles.green_line} />
              <span className={styles.blue_line} />
            </div>
          </div>
          {/* <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#1C486B",
            padding: "0.5rem 1rem",
            borderRadius: "4px",
            height: "40px",
            textTransform: "none",
          }}
          onClick={downloadReport}
        >
          Download Report
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={downloadReportPdf}>Download as PDF</MenuItem>
          <MenuItem onClick={downloadReportWord}>Download as Word</MenuItem>
        </Menu> */}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginTop: "2rem",
          }}
        >
          <table className={styles.table}>
            <tr>
              <td className={styles.table_heading}>Company Name :</td>
              <td className={styles.table_value}>
                {user?.company?.companyName}
              </td>
            </tr>
            {/* <tr>
            <td className={styles.table_heading}>User Name :</td>
            <td className={styles.table_value}>{user?.company?.admin?.name}</td>
          </tr> */}
            <tr>
              <td className={styles.table_heading}>Reporting Month :</td>
              <td className={styles.table_value}>
                {data?.reportingMonth ?? "-"}
              </td>
            </tr>
            {/* <tr>
            <td className={styles.table_heading}>Prepared By :</td>
            <td className={styles.table_value}>
              Auto Generated through Platform
            </td>
          </tr> */}
            <tr>
              <td className={styles.table_heading}>Date :</td>
              <td className={styles.table_value}>
                {new Date(data?.updatedAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </td>
            </tr>
          </table>
          <div data-ghg-report-edit-ui>
            <Button
              className={styles.download_button}
              startIcon={<DownloadIcon />}
              variant="contained"
              sx={{
                backgroundColor: "#1C486B",
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                height: "40px",
                textTransform: "none",
              }}
              onClick={downloadReport}
              data-html2canvas-ignore="true"
            >
              Download Report
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              <MenuItem onClick={downloadReportPdf}>Download as PDF</MenuItem>
              <MenuItem onClick={downloadReportWord}>Download as Word</MenuItem>
            </Menu>
          </div>
        </div>

        <div style={{ marginTop: "2rem" }} className={styles.executive_summary}>
          <p className={styles.underline_heading}>1. Executive Summary</p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginLeft: "1rem",
            }}
          >
            <div className={styles.executive_content}>
              <p className={styles.summary_heading}>Overall ESG Performance</p>
              <p className={styles.summary_subheading}>
                {data?.executiveSummary?.overallPerformance}
              </p>
              <div className={styles.comparison_card}>
                <p className={styles.summary_heading}>
                  Performance Compared to Previous Month
                </p>
                <p className={styles.summary_subheading}>
                  {data?.executiveSummary?.baselineComparison}
                </p>
              </div>
            </div>
            {/* <p className={styles.summary_heading}>
              Performance Compared to Peer Companies
            </p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.peerComparison}
            </p> */}
          </div>
        </div>

        <div className={styles.pageBreak} />
        <div
          style={{ marginTop: "2rem" }}
          className={styles.environment_section}
        >
          <p className={styles.environment_heading}>2. Environmental Metrics</p>
          <TableSectionHeading>2.1 GHG Emissions</TableSectionHeading>
          <div className={styles.direct_emission_div}>
            <p className={styles.scope_heading}>Scope 1: Direct Emissions</p>
            {/* <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            > */}
            {/* <BarChart
                labels={["Current Month", "Baseline Year"]}
                datasets={barCharDataSet}
                customWidth={250}
                customHeight={250}
              /> */}
            {/* </div> */}

            <CustomTable
              headers={scope1Headers}
              rows={scope1Rows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.scope_heading}>
              Scope 2: Indirect Emissions from Purchased Energy
            </p>
            <CustomTable
              headers={scope1Headers}
              rows={scope2Rows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.scope_heading}>
              Scope 3: Emission from Value Chain
            </p>
            <CustomTable
              headers={scope1Headers}
              rows={scope3Rows}
              month={data?.month}
            />
          </div>

          {/* Scope 4 : Avoided Emissions and Carbon Offseting */}
          <div className={styles.direct_emission_div}>
            <p className={styles.scope_heading}>
              Scope 4 : Avoided Emissions and Carbon Offseting
            </p>
            <CustomTable
              headers={scope4AvoidanceHeaders}
              rows={scope4Rows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.scope_heading}></p>
            <CustomTable
              headers={scope4OffsetHeaders}
              rows={scope4RowsForOffset}
              month={data?.month}
            />
          </div>

          <div className={styles.pageBreak} />
          <div className={styles.direct_emission_div}>
            <TableSectionHeading>2.2 Energy Consumption</TableSectionHeading>
            <CustomTable
              headers={energyConsumptionHeaders}
              rows={energyConsumptionRows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <TableSectionHeading>2.3 Water Consumption</TableSectionHeading>
            <CustomTable
              headers={waterConsumptionHeaders}
              rows={waterConsumptionRows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <TableSectionHeading>2.4 Waste Management</TableSectionHeading>
            <CustomTable
              headers={wasteManagementHeaders}
              rows={wasteManagementRows}
              month={data?.month}
            />
          </div>

          <div className={styles.pageBreak} />
          <div className={styles.direct_emission_div}>
            <TableSectionHeading>
              2.5 Upstream Transportation and Distribution
            </TableSectionHeading>
            <p className={styles.transport_table_label}>
              (a) Logistics Summary
            </p>
            <CustomTable
              headers={logisticSummaryHeaders}
              rows={upstreamLogisticRows}
              month={data?.month}
            />

            <p className={styles.transport_table_label}>
              (b) Emissions Intensity
            </p>
            <CustomTable
              headers={emissionIntensityHeaders}
              rows={upstreamEmissionIntensityRows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <TableSectionHeading>
              2.6 Downstream Transportation and Distribution
            </TableSectionHeading>
            <p className={styles.transport_table_label}>
              (a) Logistics Summary
            </p>
            <CustomTable
              headers={logisticSummaryHeaders}
              rows={downstreamLogisticRows}
              month={data?.month}
            />

            <p className={styles.transport_table_label}>
              (b) Emissions Intensity
            </p>
            <CustomTable
              headers={emissionIntensityHeaders}
              rows={downstreamEmissionIntensityRows}
              month={data?.month}
            />
          </div>
        </div>

        <div className={styles.pageBreak} />
        {data?.typeOfReport === "Consolidated" ? (
          <>
            <div className={styles.social_section}>
              <p className={styles.social_heading}>3. Social Metrics</p>

              <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>
                  3.1 Employee Well-being
                </p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={employeeWellbeingRows}
                  month={data?.month}
                />
              </div>
              {/* Diversity & Inclusion */}
              <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>
                  3.2 Diversity & Inclusion
                </p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={diversityInclusionRows}
                  month={data?.month}
                />
              </div>
              {/* Community Engagement */}
              <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>
                  3.3 Community Engagement
                </p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={communityEngagementRows}
                  month={data?.month}
                />
              </div>
              {/* Labour Practices */}
              <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>3.4 Labour Practices</p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={labourPracticesRows}
                  month={data?.month}
                />
              </div>
              {/* Human Rights */}
              <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>3.5 Human Rights</p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={humanRightsRows}
                  month={data?.month}
                />
              </div>

              {/* Training & Development */}
              <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>
                  3.6 Training & Development
                </p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={trainingDevelopmentRows}
                  month={data?.month}
                />
              </div>
              {/* Customer Satisfaction */}
              <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>
                  3.7 Customer Satisfaction
                </p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={customerSatisfactionRows}
                  month={data?.month}
                />
              </div>

              {/* <div className={styles.direct_emission_div}>
                <p className={styles.social_subheading}>3.1 Workforce Diversity</p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={socailDiversityRows}
                  month={data?.month}
                />
              </div>
              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading}>3.2 Employee Engagement</p>
                <CustomTable
                  headers={socialMetricsHeaders}
                  rows={employeeEngagementRows}
                  month={data?.month}
                />
              </div>

              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading}>3.3 Health and Safety</p>
                <CustomTable
                  headers={socialMetricsHeaders}
                  rows={healthSafetyRows}
                  month={data?.month}
                />
              </div> */}
              {/* <div style={{ marginTop: "1rem" }}>
            <p className={styles.sub_heading}>3.4 POSH Cases</p>
            <CustomTable headers={socialMetricsHeaders} rows={poshCasesRows} />
          </div> */}
            </div>

            <div className={styles.pageBreak} />
            <div className={styles.governance_section}>
              <p className={styles.governance_heading}>4. Governance Metrics</p>

              <div className={styles.direct_emission_div}>
                {/* <p className={styles.governance_subheading}> */}
                <TableSectionHeading>
                  4.1 Board Structure & Composition
                </TableSectionHeading>
                {/* </p> */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {/* <BarChart
                labels={["Current Month", "Baseline Year"]}
                datasets={barCharDataSet}
                customWidth={250}
                customHeight={250}
              /> */}
                </div>
                <CustomTable
                  headers={govtMetricsHeaders}
                  rows={boardCompositionRows}
                  month={data?.month}
                />
              </div>

              <div className={styles.direct_emission_div}>
                <p className={styles.governance_subheading}>
                  4.2 Compliance & Ethics
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {/* <BarChart
                labels={["Current Month", "Baseline Year"]}
                datasets={barCharDataSet}
                customWidth={250}
                customHeight={250}
              /> */}
                </div>
                <CustomTable
                  headers={govtMetricsHeaders}
                  rows={complianceEthicsRows}
                  month={data?.month}
                />
              </div>

              <div className={styles.direct_emission_div}>
                <p className={styles.governance_subheading}>
                  4.3 Shareholder Rights
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                ></div>
                <CustomTable
                  headers={govtMetricsHeaders}
                  rows={shareholderRightsRows}
                  month={data?.month}
                />
              </div>
              {/* Transparency & Disclosure */}
              <div className={styles.direct_emission_div}>
                <p className={styles.governance_subheading}>
                  4.4 Transparency & Disclosure
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                ></div>
                <CustomTable
                  headers={govtMetricsHeaders}
                  rows={transparencyDisclosureRows}
                  month={data?.month}
                />
              </div>

              {/* <div className={styles.direct_emission_div}>
            <div className={styles.vehicle_div}>
              <div>
                <p className={styles.sub_heading_graph}>
                  Vehicles Used by Employees
                </p>
                <p>60,000</p>
              </div>
              <div>
                <p>Vehcile Type</p>
              </div>
            </div>
          </div> */}
            </div>
          </>
        ) : null}

        <div className={styles.pageBreak} />
        {data?.typeOfReport === "Consolidated" ? (
          <div style={{ marginTop: "2rem" }}>
            <p className={styles.heading}>5. Facility-wise Analytics</p>

            <div className={styles.direct_emission_div}>
              <p className={styles.sub_heading_graph}>5.1 GHG Accounting:</p>
              <div>
                <p className={styles.sub_sub_heading}>Scope 1</p>
                <BarChart
                  labels={
                    scope1BarChartData &&
                    scope1BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope1Data}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.scope1Summary ?? summary?.scope1Summary}
                </p>
              </div>

              <div>
                <p className={styles.sub_sub_heading}>Scope 2</p>
                <BarChart
                  labels={
                    scope2BarChartData &&
                    scope2BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope2Data}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.scope2Summary ?? summary?.scope2Summary}
                </p>
              </div>

              <div className={styles.pageBreak} />
              <div>
                <p className={styles.sub_sub_heading}>Scope 3</p>
                <BarChart
                  labels={
                    scope3BarChartData &&
                    scope3BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope3Data}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.scope3Summary ?? summary?.scope3Summary}
                </p>
              </div>

              <div>
                <p className={styles.sub_sub_heading}>Scope 4</p>
                <BarChart
                  labels={
                    scope4BarChartData &&
                    scope4BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope4Data}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.scope4Summary ?? summary?.scope4Summary}
                </p>
              </div>
            </div>

            <div className={styles.direct_emission_div}>
              <p className={styles.sub_heading_graph}>5.2 Water:</p>
              <div>
                <p className={styles.sub_sub_heading}>Water Withdrawal</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility,
                    ) ?? []
                  }
                  datasets={waterWithdrawalData}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterWithdrawal ?? "-"}
                </p>
              </div>

              <div className={styles.pageBreak} />
              <div>
                <p className={styles.sub_sub_heading}>WasteWater Discharged</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility,
                    ) ?? []
                  }
                  datasets={waterDischargeData}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterDischarged ?? "-"}
                </p>
              </div>

              <div>
                <p className={styles.sub_sub_heading}>WasteWater Treated</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility,
                    ) ?? []
                  }
                  datasets={waterTreatedData}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterTreated ?? "-"}
                </p>
              </div>

              <div className={styles.pageBreak} />
              <div>
                <p className={styles.sub_sub_heading}>Water Circularity</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility,
                    ) ?? []
                  }
                  datasets={waterCircularityData}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterCircularity ?? "-"}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* <div style={{ marginTop: "2rem" }}>
          <p className={styles.heading}>6. Conclusion</p>

          <div style={{ marginLeft: "1rem" }}>
            <p className={styles.sub_heading_graph}>6.1 BRSR</p>
            <p className={styles.sub_sub_heading}>
              Reporting Status (% completed)
            </p>

            <p className={styles.sub_heading_graph}>6.2 GRI</p>
            <p className={styles.sub_sub_heading}>
              Reporting Status (% completed)
            </p>

            <p className={styles.sub_heading_graph}>6.3 CDP</p>
            <p className={styles.sub_sub_heading}>
              Reporting Status (% completed)
            </p>
          </div>
        </div> */}

        <div className={styles.pageBreak} />
        <div style={{ marginTop: "2rem" }} className={styles.conclusionSection}>
          <p className={styles.conclusionTitle}>
            {data?.typeOfReport === "Consolidated" ? 6 : 3}. Conclusion
          </p>

          <div className={styles.conclusionContent}>
            <p className={styles.conclusionSubheading}>
              Summary of key findings
            </p>
            <p className={styles.conclusionParagraph}>
              {data?.executiveSummary?.summaryOfFindings}
            </p>
            <p className={styles.conclusionSubheading}>
              Recommendations for the upcoming month
            </p>
            <p className={styles.conclusionParagraph}>
              {data?.executiveSummary?.recommendations}
            </p>
          </div>
        </div>

        <div style={{ marginTop: "3rem" }}>
          <p className={`${styles.heading} ${styles.conclusionSubheading}`}>
            Appendices qqq
          </p>

          <div style={{ marginLeft: "1rem" }}>
            <p
              className={styles.sub_main_heading}
              style={{ fontStyle: "normal" }}
            >
              <span className={styles.appendix_label}>
                Appendix A: Emission Source
              </span>
            </p>{" "}
            <ol
              className={styles.appendix_text}
              style={{ marginTop: "0.25rem", color: "#748897" }}
            >
              {(data?.sources ?? []).map((source, index) => (
                <li key={`${source}-${index}`}>{source}</li>
              ))}
            </ol>
            {/* <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix B:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixB || "Detailed Energy Consumption Data"}
              </span>
            </p>
            <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix C:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixC || "Detailed Workforce Data"}
              </span>
            </p> */}
            {/* <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix D:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixD || "Detailed Board and Governance Data"}
              </span>
            </p> */}
          </div>
          {/* <p className={styles.sub_main_heading}>
            Note: All values will be auto-filled and validated by the platform
            using AI to ensure accuracy and consistency in reporting.{" "}
          </p> */}
        </div>

        <div className={styles.company_footer}>
          <img src={companyLogo} alt="logo" width={150} />
          <p className={styles.sub_sub_heading}>Report Powered by Nirantara</p>
        </div>
      </div>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>
          We are comparing MIS Report. Please wait...
        </Typography>
      </Backdrop>
    </>
  );
};

export default MisReportCard;
